/**
 * UndoManualPaymentDialog - Confirmação para cancelar um recebimento em dinheiro
 * (estorno de baixa manual).
 *
 * Só é usado para baixas registradas MANUALMENTE pelo usuário — a RPC
 * `undo_manual_payment` recusa baixas confirmadas pelo provedor.
 * Quando a parcela está sincronizada, o estorno também é enviado ao provedor
 * (Asaas: undoReceivedInCash), reabrindo o link de pagamento.
 *
 * @author Módulo Financeiro
 * RELEASE: 2026-08-19 v1.0.0 - Criação do dialog
 */
import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Undo2 } from 'lucide-react';
import { InlineNotice } from '@/components/ui/inline-notice';
import { useUndoManualPayment } from '@/hooks/useUndoManualPayment';

interface UndoManualPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Parcela cuja baixa manual será revertida */
  parcelaId: string | null;
  /** Cobrança-pai (invalidação de cache) */
  cobrancaId: string;
  /** Número da parcela — apenas informativo */
  numeroParcela?: number | null;
  /** true quando a parcela está sincronizada com o provedor */
  isSynced?: boolean;
  /** Slug do provedor (asaas, etc.) — apenas informativo */
  providerSlug?: string | null;
}

export function UndoManualPaymentDialog({
  open,
  onOpenChange,
  parcelaId,
  cobrancaId,
  numeroParcela,
  isSynced = false,
  providerSlug,
}: UndoManualPaymentDialogProps) {
  const [reason, setReason] = useState('');
  const mutation = useUndoManualPayment();

  // Limpa o motivo sempre que o diálogo é reaberto
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const handleConfirm = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!parcelaId) return;
    try {
      await mutation.mutateAsync({
        parcela_id: parcelaId,
        cobranca_id: cobrancaId,
        reason: reason.trim() || null,
      });
      onOpenChange(false);
    } catch {
      // toast tratado no hook
    }
  };

  const submitting = mutation.isPending;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Undo2 className="h-5 w-5 text-amber-600" />
            Cancelar recebimento em dinheiro?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                A baixa manual{typeof numeroParcela === 'number' ? ` da parcela #${numeroParcela}` : ''} será
                desfeita e a cobrança voltará a ficar em aberto. O comprovante e os
                dados do pagamento anterior ficam guardados no histórico.
              </p>
              {isSynced && (
                <InlineNotice variant="warning">
                  Cobrança sincronizada com <strong>{providerSlug || 'provider'}</strong>. O
                  recebimento também será cancelado no provedor, reativando o link de pagamento.
                </InlineNotice>
              )}
              <InlineNotice variant="info">
                Depois de cancelar o recebimento, você pode usar <strong>Cancelar fatura</strong> caso
                queira remover a cobrança por completo.
              </InlineNotice>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="undo-payment-reason">Motivo (opcional)</Label>
          <Textarea
            id="undo-payment-reason"
            placeholder="Ex: baixa lançada por engano, valor incorreto…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={3}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Voltar</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={submitting || !parcelaId}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                Cancelando…
              </>
            ) : (
              <>
                <Undo2 className="h-4 w-4 mr-1.5" />
                Confirmar cancelamento
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
